import _ from 'lodash'
import type {CssValue} from '../../constants'
import {deserializeColor, serializeColor} from '../utils/color'
import {deserializeDisabled, serializeDisabled} from '../utils/disabled'
import {deserializeUnit, serializeUnit} from '../utils/unit'
import type {Stroke} from './types'

type StrokeData = Omit<Stroke, 'enabled'>
const STROKE_WIDTH_UNIT_TYPE = 'px'
const VALID_UNIT_TYPES_SET = new Set([STROKE_WIDTH_UNIT_TYPE])

const serializeStroke = ({color, opacity, width}: StrokeData): string => {
    const colorString = serializeColor({value: color, alpha: opacity?.toString()})
    const widthString = serializeUnit({value: width, type: STROKE_WIDTH_UNIT_TYPE}, VALID_UNIT_TYPES_SET)

    return `${colorString} ${widthString}`
}

const deserializeStroke = (value: string): Partial<StrokeData> | undefined => {
    if (!value) {
        return undefined
    }

    const [color, widthString] = value.split(' ')
    if (!color || !widthString) {
        return undefined
    }

    const cssColor = deserializeColor(color)
    const width = deserializeUnit(widthString, VALID_UNIT_TYPES_SET)

    if (!cssColor || !width) return undefined

    return {
        color: cssColor.value,
        opacity: cssColor.alpha ? +cssColor.alpha : undefined,
        width: width.value
    }
}

const serialize = (val: any): CssValue | undefined => {
    if (!_.isPlainObject(val)) {
        return undefined
    }

    const {color, opacity, width} = val

    if (!_.isString(color) || !_.isNumber(opacity) || !_.isNumber(width)) {
        return undefined
    }

    const strokeCss = serializeStroke(val)

    if (val.enabled) {
        return strokeCss
    }

    return serializeDisabled(strokeCss)
}

const deserialize = (rule: CssValue): Partial<Stroke> | undefined => {
    const disabledValue = deserializeDisabled(rule)

    const isEnabled = !disabledValue
    const stroke = deserializeStroke(disabledValue ?? rule)

    if (!stroke) {
        return undefined
    }

    return {
        ...stroke,
        enabled: isEnabled
    }
}

export default {
    serialize,
    deserialize
}
